import React, { useMemo } from "react";

import { LocalLogicMap, type Source, type LayerType } from "@local-logic/maps";

import type { School } from "@local-logic/client";
import { useNeighborhoodMap } from "~/sdks/NeighborhoodMap/context";

const GEOGRAPHY_LAYER_COLOR = "#008491";
const SCHOOL_CATCHMENT_COLOR = "#9333ea";

export function Layers() {
  const { geography, activeMarkerDetails } = useNeighborhoodMap();

  const primaryColor = useMemo(
    () =>
      getComputedStyle(document.documentElement).getPropertyValue(
        "--ll-colors-primary-100",
      ) || GEOGRAPHY_LAYER_COLOR,
    [],
  );

  const neighborhoodLayerSources = useMemo(() => {
    const sources: Source[] = [];

    if (
      typeof geography?.geometry === "undefined" ||
      (Array.isArray(geography.geometry) && geography.geometry.length === 0)
    ) {
      return undefined;
    }

    geography.geometry.forEach((geometry, index) => {
      sources.push({
        key: `neighborhood-geometry-${index}`,
        layer: [
          {
            type: "fill" as LayerType,
            color: primaryColor,
          },
          {
            type: "line" as LayerType,
            color: primaryColor,
          },
        ],
        geometry: [geometry],
      });
    });

    return sources;
  }, [geography, primaryColor]);

  const schoolLayerSources = useMemo(() => {
    const activeSchoolMarkerDetails = activeMarkerDetails as School;
    if (
      typeof activeSchoolMarkerDetails?.catchments === "undefined" ||
      activeSchoolMarkerDetails.catchments?.length === 0
    ) {
      return undefined;
    }

    const sources: Source[] = [];

    activeSchoolMarkerDetails.catchments.forEach(
      (catchment, catchmentIndex) => {
        if (
          Array.isArray(catchment.geometry) &&
          catchment.geometry.length > 0
        ) {
          catchment.geometry.forEach((geometry, geomIndex) => {
            sources.push({
              key: `school-catchment-${catchmentIndex}-geom-${geomIndex}`,
              layer: [
                {
                  type: "fill" as LayerType,
                  color: SCHOOL_CATCHMENT_COLOR,
                },
                {
                  type: "line" as LayerType,
                  color: SCHOOL_CATCHMENT_COLOR,
                },
              ],
              geometry: [geometry],
            });
          });
        }
      },
    );

    return sources;
  }, [activeMarkerDetails]);

  return (
    <>
      <LocalLogicMap.Layers sources={neighborhoodLayerSources} />
      <LocalLogicMap.Layers sources={schoolLayerSources} />
    </>
  );
}
